"use client";

/**
 * The tagged values of one facet cell — destinations, activity types — as a
 * comma list of links, with everything past the third behind a "+N more".
 *
 * WHY A CLIENT ISLAND IN AN OTHERWISE SERVER-RENDERED BAND. An operator with
 * eight activity types turned a 76px row of facts into a 141px one, and the
 * band's whole job is to be scannable. Showing three and counting the rest is
 * the fix QA asked for; a count with no way to open it would be worse than
 * the long list, because it says there are five more and then refuses to name
 * them.
 *
 * Every link is rendered on the server either way, so the full list is in the
 * markup from the first paint and a CRAWLER reads all of it. The island only
 * decides what is visible.
 *
 * It is not progressive enhancement, and this used to claim it was: the
 * collapsed links are hidden with `display: none` (`_experience-info.module.css`)
 * and the only thing that clears `data-collapsed` is the click handler below.
 * With JS off a reader sees three values and a button that does nothing. That
 * is the honest trade — the links are indexable, not reachable — and it is
 * written down rather than fixed because the band is a summary and every value
 * has its own page the crawler now finds.
 *
 * The separator is `aria-hidden` so a screen reader hears the names and not
 * the punctuation, which is what the server version did.
 */

import { useState } from "react";
import Link from "next/link";
import { ChevronDownIcon } from "lucide-react";

import { Button } from "@tripcart/traveler-ui/components/button";

import { useTranslator } from "@/lib/translation-context";

import styles from "./_experience-info.module.css";

/** Past this many, the rest collapse. Three is what fits one line of a cell
 *  at the band's narrowest desktop track. */
const VISIBLE = 3;

export interface FacetLink {
  slug: string;
  name: string;
  href: string;
}

/** The three classes the list draws with. The band's own are the default; the
 *  title row passes its own so the same island can sit in either surface
 *  without the band's brand-coloured link following it there.
 *
 *  `nonBreakingSeparator` is a per-surface choice, not a constant, because the
 *  two callers wrap differently: the hero's `.metaContent` is a `gap: 0` flex
 *  row, where wrapping happens BETWEEN values and an ordinary space at the end
 *  of a flex item's line box is trimmed from rendering — so a breakable comma
 *  there collapses the space entirely ("Asia,Europe"). The band's own
 *  `.facetValue` is plain block/inline flow with no flex gap, where a normal
 *  breakable space is both correct spacing AND the only thing that lets a long
 *  list wrap at all; forcing the hero's non-breaking choice onto the band left
 *  a long facet list with no break point anywhere in it, overflowing a narrow
 *  cell instead of wrapping. */
export interface FacetLinkClasses {
  value: string | undefined;
  link: string | undefined;
  more: string | undefined;
  nonBreakingSeparator?: boolean;
}

const BAND_CLASSES: FacetLinkClasses = {
  value: styles.facetValue,
  link: styles.link,
  more: styles.more,
};

export function FacetLinks({
  links,
  classes = BAND_CLASSES,
}: {
  links: FacetLink[];
  classes?: FacetLinkClasses;
}) {
  const t = useTranslator();
  const [expanded, setExpanded] = useState(false);
  const hidden = links.length - VISIBLE;
  // See `FacetLinkClasses.nonBreakingSeparator` — a `gap: 0` flex row needs a
  // real, non-collapsible space here; every other caller needs an ordinary
  // breakable one so a long list still has somewhere to wrap.
  const separator = classes.nonBreakingSeparator ? ", " : ", ";

  return (
    <>
      {links.map((link, i) => {
        // The last value this reader can currently see — either the true
        // last link, or the last VISIBLE one while the rest sit collapsed.
        // Only that one gets no trailing separator; flipping `expanded` moves
        // which index this is, so the separator appears on the old
        // last-shown value the moment the list opens.
        const isLastShown =
          i === links.length - 1 || (!expanded && i === VISIBLE - 1);
        return (
          <span
            key={link.slug}
            // `data-collapsed` rather than `hidden`: the names stay in the markup
            // for a crawler and for a reader whose JS never arrives, and the
            // stylesheet can fade them in. `hidden` cannot be animated — it is
            // `display: none`, and there is no transition out of that.
            data-collapsed={!expanded && i >= VISIBLE ? "" : undefined}
            // Only the names that were behind the count animate in. Nothing
            // moves on first paint, because on first paint they are collapsed.
            data-revealed={expanded && i >= VISIBLE ? "" : undefined}
            className={classes.value}
          >
            <Link
              href={link.href}
              className={classes.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {link.name}
            </Link>
            {/* Trails the value it follows rather than leading the next one.
                A leading separator put the comma on whichever value started a
                new line of a `flex-wrap` row like the hero's `.metaContent` —
                each value is one flex item, wrapping happens BETWEEN items,
                and a comma living at the START of the next item's own span
                stranded it there: "Asia, Europe\n, Iceland". Trailing keeps
                the comma in the span it belongs to, so a wrap can only ever
                end a line with it, never start one. The separator itself
                (nbsp or a plain space) is `classes.nonBreakingSeparator`'s
                call — see that field's doc comment above. */}
            {!isLastShown ? <span aria-hidden>{separator}</span> : null}
          </span>
        );
      })}
      {/* A REAL SPACE, not a margin. In the band the control is a word at the
          end of a sentence of words, so it has to wrap like one — and a space
          collapses at a line break where a margin would survive it as a stray
          indent. The hero's row is a flex container, which drops whitespace
          between items, so it keeps its own margin (`title-block.module.css`). */}
      {hidden > 0 ? (
        <>
          {" "}
          <Button
            variant="link"
            size="sm"
            className={classes.more}
            // The collapsed names are present but not rendered, so this is a
            // disclosure over them rather than a link somewhere.
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? t("Show less") : t("+{n} more", { n: hidden })}
            {/* The caret says which way the list moves, and it is what makes
                this read as a control rather than another activity name. It is
                decoration for a screen reader — `aria-expanded` above already
                states the same thing, and far better. */}
            <ChevronDownIcon aria-hidden strokeWidth={1.75} />
          </Button>
        </>
      ) : null}
    </>
  );
}
